import React from 'react';
import './BrandFunnel.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsOverviewResponse } from '@brand24-original/llm-types';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { percentOf } from '../../helpers/overviewInsightHelpers';

interface BrandFunnelProps {
  overview: InsightsOverviewResponse | null;
  isLoading: boolean;
  isError?: boolean;
}

const BrandFunnel: React.FC<BrandFunnelProps> = ({ overview, isLoading, isError }) => {
  const { t } = useTranslation();
  const total = overview?.totalResponses ?? 0;
  const visible = overview?.visibleResponses ?? 0;
  // StatsCache entries written before the contract extension (up to 90 s after deploy) lack the
  // top3/top1 fields — the fallback then shows real visibility and zero Top 3/Top 1 until the entry expires.
  const top3 = overview?.top3Responses ?? 0;
  const top1 = overview?.top1Responses ?? 0;

  const rows = [
    { key: 'visibility', label: t('insightPage.funnel.rowVisibility'), pct: percentOf(visible, total) },
    { key: 'top3', label: t('insightPage.funnel.rowTop3'), pct: percentOf(top3, total) },
    { key: 'top1', label: t('insightPage.funnel.rowTop1'), pct: percentOf(top1, total) },
  ];

  return (
    <div className="stats__panel brand-funnel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.funnel.title')}
          <CustomTooltip tooltipText="insightPage.funnel.tooltip" addMargin={false} />
        </div>
      </div>
      <div className="brand-funnel__card">
        {isLoading ? (
          <div className="brand-funnel__skeleton" />
        ) : isError ? (
          <EmptyPanel textKey="somethingWentWrong" />
        ) : !overview || overview.totalResponses === 0 ? (
          <EmptyPanel />
        ) : (
          <div className="brand-funnel__rows">
            {rows.map((row) => (
              <div key={row.key} className="brand-funnel__row">
                <span className="brand-funnel__label">{row.label}</span>
                <span className="brand-funnel__track">
                  {/* Min. 8% width as in the mock so the % value fits inside the fill — but at zero the
                      fill disappears: 8% of blue labelled "0%" would visually lie. */}
                  {row.pct > 0 && (
                    <span
                      className={`brand-funnel__fill brand-funnel__fill--${row.key}`}
                      style={{ width: `${Math.max(row.pct, 8)}%` }}
                    >
                      {row.pct}%
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandFunnel;
